import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Heart, MapPin, Search } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { GOOGLE_PLAY_URL } from "@/lib/config";

const EASE = [0.22, 1, 0.36, 1];
const API_URL = process.env.REACT_APP_BACKEND_URL;

const GooglePlayIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
        <path d="M4 2.5 20.5 12 13.2 12Z" fill="#00F076" />
        <path d="M4 21.5 20.5 12 13.2 12Z" fill="#FFCE00" />
        <path d="M4 2.5 4 21.5 13.2 12Z" fill="#00D2FF" />
        <path d="M16.9 9.93 20.5 12 16.9 14.07Z" fill="#FF3A44" />
    </svg>
);

const AppleIcon = ({ className }) => (
    <svg viewBox="0 0 384 512" className={className} aria-hidden="true" fill="currentColor">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
);

const PhoneMockup = () => (
    <div className="relative w-[270px] rounded-[2.6rem] border-[6px] border-brand-ink bg-brand-ink shadow-2xl shadow-black/50 sm:w-[300px]">
        <div className="overflow-hidden rounded-[2rem] bg-brand-cream">
            <div className="flex items-center justify-between px-4 pt-3">
                <span className="font-sans text-[10px] font-bold text-brand-navy/60">9:41</span>
                <span className="h-4 w-16 rounded-full bg-brand-ink" />
                <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-navy/40" />
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-navy/40" />
                </span>
            </div>
            <div className="flex items-center gap-2 px-4 pt-3">
                <LogoMark size={22} />
                <span className="font-sans text-sm font-extrabold tracking-tight text-brand-navy">
                    Puertalia<span className="text-brand-orange">.</span>
                </span>
            </div>
            <div className="mx-4 mt-3 flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-sm">
                <Search className="h-3.5 w-3.5 text-brand-navy/40" />
                <span className="font-sans text-[11px] text-brand-navy/50">¿Dónde quieres vivir?</span>
            </div>
            <div className="mt-3 flex gap-2 px-4">
                <span className="rounded-full bg-brand-orange px-3 py-1 font-sans text-[10px] font-bold text-white">Alquiler</span>
                <span className="rounded-full bg-brand-navy/10 px-3 py-1 font-sans text-[10px] font-bold text-brand-navy">Venta</span>
            </div>
            <div className="mt-3 space-y-3 px-4 pb-5">
                <div className="overflow-hidden rounded-2xl bg-white shadow-md">
                    <div className="relative h-24">
                        <img
                            src="https://images.pexels.com/photos/7851906/pexels-photo-7851906.jpeg?auto=compress&cs=tinysrgb&w=600"
                            alt="Piso luminoso en alquiler"
                            className="h-full w-full object-cover"
                        />
                        <span className="absolute left-2 top-2 rounded-full bg-brand-orange px-2 py-0.5 font-sans text-[10px] font-bold text-white">
                            750 €/mes
                        </span>
                        <Heart className="absolute right-2 top-2 h-4 w-4 fill-white text-white" />
                    </div>
                    <div className="px-3 py-2">
                        <p className="font-sans text-[11px] font-bold text-brand-navy">Piso luminoso · 2 hab.</p>
                        <p className="flex items-center gap-1 font-sans text-[10px] text-brand-navy/50">
                            <MapPin className="h-2.5 w-2.5" /> Valencia
                        </p>
                    </div>
                </div>
                <div className="overflow-hidden rounded-2xl bg-white shadow-md">
                    <div className="relative h-24">
                        <img
                            src="https://images.pexels.com/photos/3762372/pexels-photo-3762372.jpeg?auto=compress&cs=tinysrgb&w=600"
                            alt="Casa en venta"
                            className="h-full w-full object-cover"
                        />
                        <span className="absolute left-2 top-2 rounded-full bg-brand-navy px-2 py-0.5 font-sans text-[10px] font-bold text-white">
                            189.000 €
                        </span>
                        <Heart className="absolute right-2 top-2 h-4 w-4 fill-white text-white" />
                    </div>
                    <div className="px-3 py-2">
                        <p className="font-sans text-[11px] font-bold text-brand-navy">Casa con patio · 3 hab.</p>
                        <p className="flex items-center gap-1 font-sans text-[10px] text-brand-navy/50">
                            <MapPin className="h-2.5 w-2.5" /> Sevilla
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const Hero = () => {
    const { scrollY } = useScroll();
    const phoneY = useTransform(scrollY, [0, 700], [0, 90]);
    const glowY = useTransform(scrollY, [0, 700], [0, -60]);
    const [apk, setApk] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/api/apk`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (d?.available) setApk(d);
            })
            .catch(() => {});
    }, []);

    // Si hay APK subida desde el panel, el botón la descarga directamente.
    // Si no, usa GOOGLE_PLAY_URL de src/lib/config.js ("#" hasta tener la URL de la tienda).
    const playHref = apk ? `${API_URL}/api/apk/descargar` : GOOGLE_PLAY_URL;
    const apkSize = apk ? `${(apk.size / 1048576).toFixed(1).replace(".", ",")} MB` : null;

    return (
        <section
            className="relative overflow-hidden bg-brand-navy text-white"
            data-testid="hero-section"
        >
            <motion.div
                aria-hidden="true"
                style={{ y: glowY }}
                className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-brand-orange/25 blur-[140px]"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-56 -left-40 h-[480px] w-[480px] rounded-full bg-brand-wa/10 blur-[140px]"
            />
            <span
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-10 left-0 select-none whitespace-nowrap font-sans text-[22vw] font-black leading-none tracking-tighter text-white/[0.03]"
            >
                Puertalia
            </span>

            <div className="relative mx-auto grid max-w-6xl gap-14 px-5 pb-24 pt-16 sm:pt-24 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-32">
                <div>
                    <motion.span
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 font-sans text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur"
                        data-testid="hero-label"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
                        Alquiler y venta · Toda España
                    </motion.span>

                    <h1
                        className="mt-6 font-sans text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl"
                        data-testid="hero-title"
                    >
                        {["Tu próximo hogar,", "en tu bolsillo."].map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "115%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: EASE }}
                                >
                                    {i === 1 ? (
                                        <>
                                            en tu <span className="text-brand-orange">bolsillo.</span>
                                        </>
                                    ) : (
                                        line
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
                        data-testid="hero-subtitle"
                    >
                        Descarga la app de Puertalia Portal y encuentra pisos y casas en
                        alquiler o venta en toda España, a los mejores precios.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <motion.a
                            href={playHref}
                            data-testid="hero-google-play-btn"
                            whileHover={{ scale: 1.04, y: -2 }}
                            whileTap={{ scale: 0.97 }}
                            className="flex items-center gap-3 rounded-xl bg-black px-5 py-3 shadow-xl shadow-black/40 ring-1 ring-white/20 transition-shadow duration-200 hover:ring-white/40"
                        >
                            <GooglePlayIcon className="h-7 w-7" />
                            <span className="text-left leading-tight">
                                <span className="block font-sans text-[10px] font-medium uppercase tracking-wider text-white/60">
                                    Disponible en
                                </span>
                                <span className="block font-sans text-lg font-bold text-white">
                                    Google Play
                                </span>
                            </span>
                        </motion.a>

                        <div
                            data-testid="hero-app-store-btn"
                            aria-disabled="true"
                            title="Próximamente en App Store"
                            className="flex cursor-not-allowed select-none items-center gap-3 rounded-xl bg-black px-5 py-3 opacity-40 ring-1 ring-white/15 grayscale"
                        >
                            <AppleIcon className="h-7 w-7 text-white" />
                            <span className="text-left leading-tight">
                                <span className="block font-sans text-[10px] font-medium uppercase tracking-wider text-white/60">
                                    Próximamente en
                                </span>
                                <span className="block font-sans text-lg font-bold text-white">
                                    App Store
                                </span>
                            </span>
                        </div>

                        <motion.a
                            href="#como-funciona"
                            data-testid="hero-como-funciona-btn"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.97 }}
                            className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 font-sans text-sm font-bold text-white transition-colors duration-200 hover:border-brand-orange hover:text-brand-orange"
                        >
                            Cómo funciona
                            <ArrowDown className="h-4 w-4" />
                        </motion.a>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1 }}
                        className="mt-8 space-y-2"
                    >
                        <p className="text-sm font-bold text-white/90" data-testid="hero-trust-line">
                            ✅ No pedimos dinero ni documentación. Todo se hace en persona.
                        </p>
                        <p className="text-xs text-white/55" data-testid="hero-android-note">
                            📱 Por ahora la app solo funciona en Android. Versión para iPhone, próximamente.
                        </p>
                        {apk && (
                            <p className="text-xs font-bold text-brand-wa" data-testid="hero-apk-ready">
                                Descarga directa disponible · {apkSize}
                            </p>
                        )}
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 60, rotate: 4 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                    className="relative mx-auto lg:mx-0 lg:justify-self-end"
                >
                    <motion.div style={{ y: phoneY }}>
                        <motion.div
                            animate={{ y: [0, -12, 0] }}
                            transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <PhoneMockup />
                        </motion.div>
                    </motion.div>
                    <div
                        aria-hidden="true"
                        className="absolute -inset-8 -z-10 rounded-full bg-brand-orange/20 blur-3xl"
                    />
                </motion.div>
            </div>
        </section>
    );
};
